import {StyleSheet} from "aphrodite";
import * as React from "react";

import {addStyle, type StyleType} from "@khanacademy/wonder-blocks-core";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";

import AskKhanmigoMenu from "../ask-khanmigo-menu.tsx";
import type {JsonPath} from "../types.ts";
import {useCoeditingUI} from "../use-coediting-ui.ts";
import {equalPaths} from "../utils.ts";

const StyledDiv = addStyle("div");

export default function EditableItem(props: {
    path: JsonPath;
    style?: StyleType;
    tag?: React.ForwardRefExoticComponent<any>;
    children: React.ReactNode;
}) {
    const {path, style, tag, children} = props;

    const {selectedPath, hoveredPath, viewOnly} = useCoeditingUI();

    const isSelected = equalPaths(path, selectedPath);
    const isHovered = equalPaths(path, hoveredPath);

    const Tag = tag || StyledDiv;

    return (
        <Tag
            role="treeitem"
            aria-selected={isSelected}
            data-coeditable={JSON.stringify(path)}
            style={[
                styles.item,
                style,
                isSelected && styles.selected,
                isHovered && styles.hovered,
            ]}
        >
            {children}
            {isSelected && !viewOnly && <AskKhanmigoMenu path={path} />}
        </Tag>
    );
}

const styles = StyleSheet.create({
    item: {
        position: "relative",
        borderRadius: border.radius.radius_040,
        padding: sizing.size_040,
        margin: `calc(-1 * ${sizing.size_040})`,
        cursor: "pointer",
        transition: "background-color 50ms ease-in, transform 0.1s ease-in",
        ":focus": {
            outlineOffset: sizing.size_020,
        },
    },
    hovered: {
        backgroundColor: semanticColor.khanmigo.secondary,
        "@media print": {
            backgroundColor: "transparent",
        },
    },
    selected: {
        transform: "scale(1.01)",
        boxShadow: "0 2px 4px #ccc",
        backgroundColor: semanticColor.khanmigo.secondary,
        marginInlineStart: -14,
        paddingInlineStart: 14,
        marginBlockStart: `calc(-1 * ${sizing.size_080})`,
        paddingBlockStart: sizing.size_080,
        marginBlockEnd: `calc(-1 * ${sizing.size_080})`,
        paddingBlockEnd: sizing.size_080,
        "::before": {
            content: "''",
            display: "block",
            position: "absolute",
            borderTopLeftRadius: border.radius.radius_040,
            borderBottomLeftRadius: border.radius.radius_040,
            top: 0,
            bottom: 0,
            left: 0,
            backgroundColor: semanticColor.khanmigo.primary,
            width: 6,
        },
        "@media print": {
            backgroundColor: "transparent",
            boxShadow: "none",
            transform: "none",
        },
    },
});
